import { useEffect, useState } from 'react';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import { GridRowModes, GridToolbarContainer, GridActionsCellItem, useGridApiRef } from '@mui/x-data-grid-premium';
import { Field, FieldArray, useFormikContext } from 'formik';
import { v4 as uuidv4 } from 'uuid';
import { Box, Typography, Paper } from '@mui/material';
import dayjs from 'dayjs';

import { dataGridPremiumColumns } from '../../utils/helper';
import {
  AutoCompleteSelectUI,
  DataGridIdentifiers,
  FormErrorUI,
  AddButtonUi,
  DatagridPremiumUi,
  InputUI,
  DatePickerUI,
  ButtonUI,
} from '../Interface';

import { InvManagementMethod } from '../../config/itemGroup';
import warehouseService from '../../service/warehouse.service';

const BatchToolbar = (props) => {
  const { disabled, push, defaultBatchLine, apiRef, selectedLineIndex } = props;
  const { values, validateField } = useFormikContext();

  const handleClick = async () => {
    const newLine = { ...defaultBatchLine, id: uuidv4() };
    await push(newLine);
    apiRef.current.startRowEditMode({ id: newLine.id });

    const fieldsToValidate = [
      `docLine.${selectedLineIndex}.docLineBatchSn.${values.docLine[selectedLineIndex]?.docLineBatchSn?.length ?? 0}.batchSn`,
      `docLine.${selectedLineIndex}.docLineBatchSn.${values.docLine[selectedLineIndex]?.docLineBatchSn?.length ?? 0}.quantity`,
    ];

    for (const field of fieldsToValidate) {
      await validateField(field);
    }
  };

  return (
    <GridToolbarContainer>
      <AddButtonUi disabled={disabled} onClick={handleClick} />
    </GridToolbarContainer>
  );
};

const defaultBinLine = {
  id: null,
  binCode: '',
  binId: null,
  snBinId: null,
  quantity: '',
};

const BatchIn = ({ disabled, autocompleteData, selectedLine, defaultBatchLine, onClose, open }) => {
  const apiRef = useGridApiRef();
  const { values, validateField, setFieldValue, errors } = useFormikContext();
  const [binOptions, setBinOptions] = useState([]);

  const selectedLineIndex = selectedLine?.selectedLineIndex;
  const item = autocompleteData?.item?.find((i) => i.itemCode === values.docLine[selectedLineIndex]?.itemCode);
  const isBatchManaged = item?.invManagementMethod === InvManagementMethod.BATCH;
  const whsCode = values.docLine[selectedLineIndex]?.whsCode ?? values.whsCode;
  const whs = autocompleteData?.warehouse?.find((w) => w.code === whsCode);
  const isBinManaged = Boolean(whs?.binManaged);

  useEffect(() => {
    if (typeof selectedLineIndex !== 'number') return;
    if (!open || !whsCode) return;
    warehouseService.getBins(whsCode).then((data) => {
      const options = (data ?? []).map((bin) => ({
        label: bin.binCode,
        value: bin.binId,
        binCode: bin.binCode,
      }));
      setBinOptions(options);
    });
  }, [open, selectedLineIndex, whsCode]);

  if (!selectedLine || typeof selectedLineIndex !== 'number') return null;

  const getLineIndex = (lines, id) => {
    return lines.findIndex((rc) => rc.id === id);
  };

  const isNegativeNumberInput = (value) => {
    if (value === '' || value === null || value === undefined) return false;

    const parsed = Number(value);
    return !Number.isNaN(parsed) && parsed < 0;
  };

  const isNonPositiveNumber = (value) => {
    if (value === '' || value === null || value === undefined) return false;

    const parsed = Number(value);
    return !Number.isNaN(parsed) && parsed <= 0;
  };

  const handleAddBin = async (push) => {
    const newLine = { ...defaultBinLine, id: uuidv4() };
    await push(newLine);
    apiRef.current.startRowEditMode({ id: newLine.id });
  };

  const BinToolbar = ({ disabled, push }) => (
    <GridToolbarContainer>
      <AddButtonUi disabled={disabled} onClick={() => handleAddBin(push)} />
    </GridToolbarContainer>
  );

  const upsertBinForBatch = (batchId, binCode, binId, quantity) => {
    const bins = [...(values.docLine[selectedLineIndex]?.docLineBin ?? [])];
    const idx = bins.findIndex((b) => b.snBinId === batchId);
    const updated = {
      ...(idx >= 0 ? bins[idx] : { id: uuidv4() }),
      binCode,
      binId,
      snBinId: batchId,
      quantity,
    };

    if (idx >= 0) {
      bins[idx] = updated;
    } else {
      bins.push(updated);
    }

    setFieldValue(`docLine.${selectedLineIndex}.docLineBin`, bins);
  };

  const columns = (setFieldValue, validateField, errors, remove) =>
    dataGridPremiumColumns([
      ...(isBinManaged
        ? [
            {
              field: 'binCode',
              headerName: 'Rakhely',
              type: 'string',
              editable: true,
              sortable: false,
              valueGetter: (_data, row) => {
                // Batch-menedzselt esetben a rakhely hozzárendelés a docLineBin-ben van (snBinId = batch row.id)
                if (!isBatchManaged) return row?.binCode ?? '';
                const key = row?.id ?? null;
                if (!key) return '';
                const bin = (values.docLine[selectedLineIndex]?.docLineBin ?? []).find((b) => b.snBinId === key);
                return bin?.binCode ?? '';
              },
              renderCell: (data) => {
                // A valueGetter-t használjuk, hogy view módban is mindig megjelenjen
                if (!isBatchManaged) return data.value ?? '';
                const key = data?.row?.id ?? null;
                if (!key) return '';
                const bin = (values.docLine[selectedLineIndex]?.docLineBin ?? []).find((b) => b.snBinId === key);
                return bin?.binCode ?? '';
              },
              renderEditCell: (data) => {
                if (isBatchManaged) {
                  const batchIndex = getLineIndex(values.docLine[selectedLineIndex].docLineBatchSn, data.id);
                  const key = data?.id ?? null;
                  const bin = key
                    ? (values.docLine[selectedLineIndex]?.docLineBin ?? []).find((b) => b.snBinId === key)
                    : null;
                  const binCode = bin?.binCode ?? '';
                  return (
                    <Field
                      name={`docLine.${selectedLineIndex}.docLineBatchSn.${batchIndex}.binCode`}
                      component={AutoCompleteSelectUI}
                      variant="standard"
                      fullWidth
                      value={binOptions.find((opt) => opt.binCode === binCode) ?? null}
                      dataset={binOptions}
                      helperText={
                        <FormErrorUI
                          message={errors?.docLine?.[selectedLineIndex]?.docLineBin?.[batchIndex]?.binCode}
                        />
                      }
                      onChange={(_e, newVal) => {
                        const newBinCode = newVal?.binCode ?? '';
                        const newBinId = newVal?.value ?? null;
                        upsertBinForBatch(
                          data.id,
                          newBinCode,
                          newBinId,
                          values.docLine[selectedLineIndex]?.docLineBatchSn?.[batchIndex]?.quantity ?? ''
                        );
                        data.api.setEditCellValue({
                          id: data.id,
                          field: data.field,
                          value: newBinCode,
                        });
                      }}
                    />
                  );
                }

                const lineIndex = getLineIndex(values.docLine[selectedLineIndex].docLineBin, data.id);
                const binCode = values.docLine[selectedLineIndex]?.docLineBin?.[lineIndex]?.binCode ?? data.value ?? '';
                return (
                  <Field
                    name={`docLine.${selectedLineIndex}.docLineBin.${lineIndex}.binCode`}
                    component={AutoCompleteSelectUI}
                    variant="standard"
                    fullWidth
                    value={binOptions.find((opt) => opt.binCode === binCode) ?? null}
                    dataset={binOptions}
                    onChange={(_e, newVal) => {
                      const newBinCode = newVal?.binCode ?? '';
                      const newBinId = newVal?.value ?? null;
                      setFieldValue(`docLine.${selectedLineIndex}.docLineBin.${lineIndex}.binCode`, newBinCode);
                      setFieldValue(`docLine.${selectedLineIndex}.docLineBin.${lineIndex}.binId`, newBinId);
                      data.api.setEditCellValue({
                        id: data.id,
                        field: data.field,
                        value: newBinCode,
                      });
                    }}
                    onBlur={async () =>
                      await validateField(`docLine.${selectedLineIndex}.docLineBin.${lineIndex}.binCode`)
                    }
                  />
                );
              },
            },
          ]
        : []),
      ...(isBatchManaged
        ? [
            {
              field: 'batchSn',
              headerName: 'Sarzs',
              type: 'string',
              editable: true,
              sortable: false,
              renderEditCell: (data) => {
                const batchIndex = getLineIndex(values.docLine[selectedLineIndex].docLineBatchSn, data.id);
                return (
                  <Field
                    name={`docLine.${selectedLineIndex}.docLineBatchSn.${batchIndex}.batchSn`}
                    type="text"
                    variant="standard"
                    as={InputUI}
                    fullWidth
                    helperText={
                      <FormErrorUI
                        message={errors?.docLine?.[selectedLineIndex]?.docLineBatchSn?.[batchIndex]?.batchSn}
                      />
                    }
                    onChange={(e) => {
                      setFieldValue(
                        `docLine.${selectedLineIndex}.docLineBatchSn.${batchIndex}.batchSn`,
                        e.target.value ?? ''
                      );
                      if (isBinManaged) {
                        const batchId = values.docLine[selectedLineIndex]?.docLineBatchSn?.[batchIndex]?.id ?? null;
                        const currentBin = (values.docLine[selectedLineIndex]?.docLineBin ?? []).find(
                          (b) => b.snBinId === batchId
                        );
                        if (currentBin) {
                          upsertBinForBatch(
                            batchId,
                            currentBin.binCode ?? '',
                            currentBin.binId ?? null,
                            values.docLine[selectedLineIndex]?.docLineBatchSn?.[batchIndex]?.quantity ?? ''
                          );
                        }
                      }
                      data.api.setEditCellValue({
                        id: data.id,
                        field: data.field,
                        value: e.target.value,
                      });
                    }}
                    onBlur={async () =>
                      await validateField(`docLine.${selectedLineIndex}.docLineBatchSn.${batchIndex}.batchSn`)
                    }
                  />
                );
              },
            },
            {
              field: 'quantity',
              headerName: 'Mennyiség',
              type: 'number',
              editable: true,
              sortable: false,
              renderEditCell: (data) => {
                const batchIndex = getLineIndex(values.docLine[selectedLineIndex].docLineBatchSn, data.id);
                return (
                  <Field
                    name={`docLine.${selectedLineIndex}.docLineBatchSn.${batchIndex}.quantity`}
                    type="number"
                    variant="standard"
                    as={InputUI}
                    fullWidth
                    inputProps={{ min: 0.0000001, step: 'any' }}
                    helperText={
                      <FormErrorUI
                        message={errors?.docLine?.[selectedLineIndex]?.docLineBatchSn?.[batchIndex]?.quantity}
                      />
                    }
                    onKeyDown={(e) => {
                      if (e.key === '-') {
                        e.preventDefault();
                      }
                    }}
                    onChange={(e) => {
                      if (isNegativeNumberInput(e.target.value)) return;

                      setFieldValue(
                        `docLine.${selectedLineIndex}.docLineBatchSn.${batchIndex}.quantity`,
                        e.target.value ?? ''
                      );
                      if (isBinManaged) {
                        const batchId = values.docLine[selectedLineIndex]?.docLineBatchSn?.[batchIndex]?.id ?? null;
                        const currentBin = (values.docLine[selectedLineIndex]?.docLineBin ?? []).find(
                          (b) => b.snBinId === batchId
                        );
                        if (currentBin) {
                          upsertBinForBatch(
                            batchId,
                            currentBin.binCode ?? '',
                            currentBin.binId ?? null,
                            e.target.value ?? ''
                          );
                        }
                      }
                      data.api.setEditCellValue({
                        id: data.id,
                        field: data.field,
                        value: e.target.value,
                      });
                    }}
                    onBlur={async () => {
                      const path = `docLine.${selectedLineIndex}.docLineBatchSn.${batchIndex}.quantity`;
                      if (
                        isNonPositiveNumber(values.docLine[selectedLineIndex]?.docLineBatchSn?.[batchIndex]?.quantity)
                      ) {
                        setFieldValue(path, '');
                      }
                      await validateField(path);
                    }}
                  />
                );
              },
            },
            {
              field: 'lot',
              headerName: 'LOT',
              type: 'string',
              editable: true,
              sortable: false,
              renderEditCell: (data) => {
                const batchIndex = getLineIndex(values.docLine[selectedLineIndex].docLineBatchSn, data.id);
                return (
                  <Field
                    name={`docLine.${selectedLineIndex}.docLineBatchSn.${batchIndex}.lot`}
                    type="text"
                    variant="standard"
                    as={InputUI}
                    fullWidth
                    helperText={
                      <FormErrorUI message={errors?.docLine?.[selectedLineIndex]?.docLineBatchSn?.[batchIndex]?.lot} />
                    }
                    onChange={(e) => {
                      setFieldValue(
                        `docLine.${selectedLineIndex}.docLineBatchSn.${batchIndex}.lot`,
                        e.target.value ?? ''
                      );
                      data.api.setEditCellValue({
                        id: data.id,
                        field: data.field,
                        value: e.target.value,
                      });
                    }}
                    onBlur={async () =>
                      await validateField(`docLine.${selectedLineIndex}.docLineBatchSn.${batchIndex}.lot`)
                    }
                  />
                );
              },
            },
            {
              field: 'supplierId',
              headerName: 'Beszállító azon.',
              type: 'string',
              editable: true,
              sortable: false,
              renderEditCell: (data) => {
                const batchIndex = getLineIndex(values.docLine[selectedLineIndex].docLineBatchSn, data.id);
                return (
                  <Field
                    name={`docLine.${selectedLineIndex}.docLineBatchSn.${batchIndex}.supplierId`}
                    type="text"
                    variant="standard"
                    as={InputUI}
                    fullWidth
                    helperText={
                      <FormErrorUI
                        message={errors?.docLine?.[selectedLineIndex]?.docLineBatchSn?.[batchIndex]?.supplierId}
                      />
                    }
                    onChange={(e) => {
                      setFieldValue(
                        `docLine.${selectedLineIndex}.docLineBatchSn.${batchIndex}.supplierId`,
                        e.target.value ?? ''
                      );
                      data.api.setEditCellValue({
                        id: data.id,
                        field: data.field,
                        value: e.target.value,
                      });
                    }}
                    onBlur={async () =>
                      await validateField(`docLine.${selectedLineIndex}.docLineBatchSn.${batchIndex}.supplierId`)
                    }
                  />
                );
              },
            },
            {
              field: 'expDate',
              headerName: 'Lejárat',
              type: 'date',
              editable: true,
              sortable: false,
              valueGetter: (_data, row) => {
                const batchIndex = getLineIndex(values.docLine[selectedLineIndex].docLineBatchSn, row.id);
                return values.docLine[selectedLineIndex]?.docLineBatchSn?.[batchIndex]?.expDate
                  ? new Date(values.docLine[selectedLineIndex]?.docLineBatchSn?.[batchIndex]?.expDate)
                  : null;
              },
              renderCell: (data) => {
                const batchIndex = getLineIndex(values.docLine[selectedLineIndex].docLineBatchSn, data.row.id);
                return values.docLine[selectedLineIndex]?.docLineBatchSn?.[batchIndex]?.expDate
                  ? dayjs(values.docLine[selectedLineIndex]?.docLineBatchSn?.[batchIndex]?.expDate).format('YYYY.MM.DD')
                  : '';
              },
              renderEditCell: (data) => {
                const batchIndex = getLineIndex(values.docLine[selectedLineIndex].docLineBatchSn, data.id);
                return (
                  <Field
                    name={`docLine.${selectedLineIndex}.docLineBatchSn.${batchIndex}.expDate`}
                    type="date"
                    variant="standard"
                    component={DatePickerUI}
                    fullWidth
                    value={dayjs(values.docLine[selectedLineIndex]?.docLineBatchSn?.[batchIndex]?.expDate)}
                    helperText={
                      <FormErrorUI
                        message={errors?.docLine?.[selectedLineIndex]?.docLineBatchSn?.[batchIndex]?.expDate}
                      />
                    }
                    onChange={(e) => {
                      const newValue = e ? dayjs(e).startOf('day').add(12, 'hours').toISOString() : null;
                      setFieldValue(`docLine.${selectedLineIndex}.docLineBatchSn.${batchIndex}.expDate`, newValue);
                      data.api.setEditCellValue({
                        id: data.id,
                        field: data.field,
                        value: e ? new Date(e) : null,
                      });
                    }}
                    onBlur={async () =>
                      await validateField(`docLine.${selectedLineIndex}.docLineBatchSn.${batchIndex}.expDate`)
                    }
                  />
                );
              },
            },
            {
              field: 'mnfDate',
              headerName: 'Gyártás',
              type: 'date',
              editable: true,
              sortable: false,
              valueGetter: (_data, row) => {
                const batchIndex = getLineIndex(values.docLine[selectedLineIndex].docLineBatchSn, row.id);
                return values.docLine[selectedLineIndex]?.docLineBatchSn?.[batchIndex]?.mnfDate
                  ? new Date(values.docLine[selectedLineIndex]?.docLineBatchSn?.[batchIndex]?.mnfDate)
                  : null;
              },
              renderCell: (data) => {
                const batchIndex = getLineIndex(values.docLine[selectedLineIndex].docLineBatchSn, data.row.id);
                return values.docLine[selectedLineIndex]?.docLineBatchSn?.[batchIndex]?.mnfDate
                  ? dayjs(values.docLine[selectedLineIndex]?.docLineBatchSn?.[batchIndex]?.mnfDate).format('YYYY.MM.DD')
                  : '';
              },
              renderEditCell: (data) => {
                const batchIndex = getLineIndex(values.docLine[selectedLineIndex].docLineBatchSn, data.id);
                return (
                  <Field
                    name={`docLine.${selectedLineIndex}.docLineBatchSn.${batchIndex}.mnfDate`}
                    type="date"
                    variant="standard"
                    component={DatePickerUI}
                    fullWidth
                    value={dayjs(values.docLine[selectedLineIndex]?.docLineBatchSn?.[batchIndex]?.mnfDate)}
                    helperText={
                      <FormErrorUI
                        message={errors?.docLine?.[selectedLineIndex]?.docLineBatchSn?.[batchIndex]?.mnfDate}
                      />
                    }
                    onChange={(e) => {
                      const newValue = e ? dayjs(e).startOf('day').add(12, 'hours').toISOString() : null;
                      setFieldValue(`docLine.${selectedLineIndex}.docLineBatchSn.${batchIndex}.mnfDate`, newValue);
                      data.api.setEditCellValue({
                        id: data.id,
                        field: data.field,
                        value: e ? new Date(e) : null,
                      });
                    }}
                    onBlur={async () =>
                      await validateField(`docLine.${selectedLineIndex}.docLineBatchSn.${batchIndex}.mnfDate`)
                    }
                  />
                );
              },
            },
            {
              field: 'batchAttribute1',
              headerName: 'Attribútum 1',
              type: 'string',
              editable: true,
              sortable: false,
              renderEditCell: (data) => {
                const batchIndex = getLineIndex(values.docLine[selectedLineIndex].docLineBatchSn, data.id);
                return (
                  <Field
                    name={`docLine.${selectedLineIndex}.docLineBatchSn.${batchIndex}.batchAttribute1`}
                    type="text"
                    variant="standard"
                    as={InputUI}
                    fullWidth
                    helperText={
                      <FormErrorUI
                        message={errors?.docLine?.[selectedLineIndex]?.docLineBatchSn?.[batchIndex]?.batchAttribute1}
                      />
                    }
                    onChange={(e) => {
                      setFieldValue(
                        `docLine.${selectedLineIndex}.docLineBatchSn.${batchIndex}.batchAttribute1`,
                        e.target.value ?? ''
                      );
                      data.api.setEditCellValue({
                        id: data.id,
                        field: data.field,
                        value: e.target.value,
                      });
                    }}
                    onBlur={async () =>
                      await validateField(`docLine.${selectedLineIndex}.docLineBatchSn.${batchIndex}.batchAttribute1`)
                    }
                  />
                );
              },
            },
            {
              field: 'batchAttribute2',
              headerName: 'Attribútum 2',
              type: 'string',
              editable: true,
              sortable: false,
              renderEditCell: (data) => {
                const batchIndex = getLineIndex(values.docLine[selectedLineIndex].docLineBatchSn, data.id);
                return (
                  <Field
                    name={`docLine.${selectedLineIndex}.docLineBatchSn.${batchIndex}.batchAttribute2`}
                    type="text"
                    variant="standard"
                    as={InputUI}
                    fullWidth
                    helperText={
                      <FormErrorUI
                        message={errors?.docLine?.[selectedLineIndex]?.docLineBatchSn?.[batchIndex]?.batchAttribute2}
                      />
                    }
                    onChange={(e) => {
                      setFieldValue(
                        `docLine.${selectedLineIndex}.docLineBatchSn.${batchIndex}.batchAttribute2`,
                        e.target.value ?? ''
                      );
                      data.api.setEditCellValue({
                        id: data.id,
                        field: data.field,
                        value: e.target.value,
                      });
                    }}
                    onBlur={async () =>
                      await validateField(`docLine.${selectedLineIndex}.docLineBatchSn.${batchIndex}.batchAttribute2`)
                    }
                  />
                );
              },
            },
            {
              field: 'comment',
              headerName: 'Megjegyzés',
              type: 'string',
              editable: true,
              sortable: false,
              renderEditCell: (data) => {
                const batchIndex = getLineIndex(values.docLine[selectedLineIndex].docLineBatchSn, data.id);
                return (
                  <Field
                    name={`docLine.${selectedLineIndex}.docLineBatchSn.${batchIndex}.comment`}
                    type="text"
                    variant="standard"
                    as={InputUI}
                    fullWidth
                    helperText={
                      <FormErrorUI
                        message={errors?.docLine?.[selectedLineIndex]?.docLineBatchSn?.[batchIndex]?.comment}
                      />
                    }
                    onChange={(e) => {
                      setFieldValue(
                        `docLine.${selectedLineIndex}.docLineBatchSn.${batchIndex}.comment`,
                        e.target.value ?? ''
                      );
                      data.api.setEditCellValue({
                        id: data.id,
                        field: data.field,
                        value: e.target.value,
                      });
                    }}
                    onBlur={async () =>
                      await validateField(`docLine.${selectedLineIndex}.docLineBatchSn.${batchIndex}.comment`)
                    }
                  />
                );
              },
            },
            {
              field: 'price',
              headerName: 'Ár',
              type: 'string',
              editable: true,
              sortable: false,
              valueGetter: (data, row) => {
                const batchIndex = getLineIndex(values.docLine[selectedLineIndex].docLineBatchSn, row.id);
                return values.docLine[selectedLineIndex]?.docLineBatchSn?.[batchIndex]?.price;
              },
              renderEditCell: (data) => {
                const batchIndex = getLineIndex(values.docLine[selectedLineIndex].docLineBatchSn, data.id);
                return (
                  <Field
                    name={`docLine.${selectedLineIndex}.docLineBatchSn.${batchIndex}.price`}
                    type="text"
                    variant="standard"
                    as={InputUI}
                    fullWidth
                    helperText={
                      <FormErrorUI
                        message={errors?.docLine?.[selectedLineIndex]?.docLineBatchSn?.[batchIndex]?.price}
                      />
                    }
                    onChange={(e) => {
                      setFieldValue(
                        `docLine.${selectedLineIndex}.docLineBatchSn.${batchIndex}.price`,
                        e.target.value ?? ''
                      );
                      data.api.setEditCellValue({
                        id: data.id,
                        field: data.field,
                        value: e.target.value,
                      });
                    }}
                    onBlur={async () =>
                      await validateField(`docLine.${selectedLineIndex}.docLineBatchSn.${batchIndex}.price`)
                    }
                  />
                );
              },
            },
            {
              field: 'other',
              headerName: 'Egyéb',
              type: 'string',
              editable: true,
              sortable: false,
              renderEditCell: (data) => {
                const batchIndex = getLineIndex(values.docLine[selectedLineIndex].docLineBatchSn, data.id);
                return (
                  <Field
                    name={`docLine.${selectedLineIndex}.docLineBatchSn.${batchIndex}.other`}
                    type="text"
                    variant="standard"
                    as={InputUI}
                    fullWidth
                    helperText={
                      <FormErrorUI
                        message={errors?.docLine?.[selectedLineIndex]?.docLineBatchSn?.[batchIndex]?.other}
                      />
                    }
                    onChange={(e) => {
                      setFieldValue(
                        `docLine.${selectedLineIndex}.docLineBatchSn.${batchIndex}.other`,
                        e.target.value ?? ''
                      );
                      data.api.setEditCellValue({
                        id: data.id,
                        field: data.field,
                        value: e.target.value,
                      });
                    }}
                    onBlur={async () =>
                      await validateField(`docLine.${selectedLineIndex}.docLineBatchSn.${batchIndex}.other`)
                    }
                  />
                );
              },
            },
          ]
        : [
            {
              field: 'quantity',
              headerName: 'Mennyiség',
              type: 'number',
              editable: true,
              sortable: false,
              renderEditCell: (data) => {
                const lineIndex = getLineIndex(values.docLine[selectedLineIndex].docLineBin, data.id);
                return (
                  <Field
                    name={`docLine.${selectedLineIndex}.docLineBin.${lineIndex}.quantity`}
                    type="number"
                    variant="standard"
                    as={InputUI}
                    fullWidth
                    inputProps={{ min: 0.0000001, step: 'any' }}
                    helperText={
                      <FormErrorUI message={errors?.docLine?.[selectedLineIndex]?.docLineBin?.[lineIndex]?.quantity} />
                    }
                    onKeyDown={(e) => {
                      if (e.key === '-') {
                        e.preventDefault();
                      }
                    }}
                    onChange={(e) => {
                      if (isNegativeNumberInput(e.target.value)) return;

                      setFieldValue(
                        `docLine.${selectedLineIndex}.docLineBin.${lineIndex}.quantity`,
                        e.target.value ?? ''
                      );
                      data.api.setEditCellValue({
                        id: data.id,
                        field: data.field,
                        value: e.target.value,
                      });
                    }}
                    onBlur={async () => {
                      const path = `docLine.${selectedLineIndex}.docLineBin.${lineIndex}.quantity`;
                      if (isNonPositiveNumber(values.docLine[selectedLineIndex]?.docLineBin?.[lineIndex]?.quantity)) {
                        setFieldValue(path, '');
                      }
                      await validateField(path);
                    }}
                  />
                );
              },
            },
          ]),
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Szerk.',
        cellClassName: 'actions',
        sortable: false,
        getActions: ({ id }) => [
          <GridActionsCellItem
            icon={<DeleteIcon />}
            label="Delete"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              if (isBatchManaged) {
                // Sarzs törlés
                const batchSnLines = values.docLine[selectedLineIndex].docLineBatchSn || [];
                const binLines = values.docLine[selectedLineIndex].docLineBin || [];
                const lineIndex = getLineIndex(batchSnLines, id);
                const batch = batchSnLines[lineIndex];
                // Töröljük a kapcsolódó bin hozzárendelést is
                if (batch && batch.id) {
                  const newBinLines = binLines.filter((b) => b.snBinId !== batch.id);
                  setFieldValue(`docLine.${selectedLineIndex}.docLineBin`, newBinLines);
                }
                remove(lineIndex);
              } else {
                // Bin törlés
                const binLines = values.docLine[selectedLineIndex].docLineBin || [];
                const batchSnLines = values.docLine[selectedLineIndex].docLineBatchSn || [];
                const lineIndex = getLineIndex(binLines, id);
                const bin = binLines[lineIndex];
                // Ha van kapcsolódó sarzs, azt is töröljük
                if (bin && bin.snBinId) {
                  const newBatchSnLines = batchSnLines.filter((b) => b.id !== bin.snBinId);
                  setFieldValue(`docLine.${selectedLineIndex}.docLineBatchSn`, newBatchSnLines);
                }
                remove(lineIndex);
              }
            }}
            color="inherit"
            disabled={disabled}
          />,
        ],
      },
    ]);

  const handleSave = () => {
    onClose();
  };

  const handleCancel = () => {
    setFieldValue(`docLine.${selectedLine.selectedLineIndex}.docLineBatchSn`, selectedLine.docLineBatchSn || []);
    setFieldValue(`docLine.${selectedLine.selectedLineIndex}.docLineBin`, selectedLine.docLineBin || []);
    onClose();
  };

  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Box className="align-middle">
        <Paper elevation={1} sx={{ overflow: 'hidden', borderRadius: 2 }}>
          <Box display="flex" gap={2} p={2} alignItems="center" bgcolor="#f5f5f5" mb={2}>
            <Box flex={0.35} minWidth={0} display="flex" alignItems="center">
              <Typography fontWeight="bold">Sor:&nbsp; </Typography>
              <Typography>{selectedLineIndex + 1}</Typography>
            </Box>
            <Box flex={1} minWidth={0} display="flex" alignItems="center">
              <Typography fontWeight="bold">Cikkszám:&nbsp; </Typography>
              <Typography>{values.docLine[selectedLineIndex]?.itemCode ?? ''}</Typography>
            </Box>
            <Box flex={1} minWidth={0} display="flex" alignItems="center">
              <Typography fontWeight="bold">Raktár:&nbsp; </Typography>
              <Typography>{values.docLine[selectedLineIndex]?.whsCode ?? ''}</Typography>
            </Box>
            <Box flex={1} minWidth={0} display="flex" alignItems="center">
              <Typography fontWeight="bold">Nyitott mennyiség:&nbsp; </Typography>
              <Typography>{values.docLine[selectedLineIndex]?.qty ?? ''}</Typography>
            </Box>
            <Box flex={1} minWidth={0} display="flex" alignItems="center">
              <Typography fontWeight="bold">
                {isBatchManaged ? 'Összesített sarzs: ' : 'Összesített rakhely: '}&nbsp;
              </Typography>
              <Typography>
                {(isBatchManaged
                  ? values.docLine[selectedLineIndex]?.docLineBatchSn ?? []
                  : values.docLine[selectedLineIndex]?.docLineBin ?? []
                ).reduce((sum, line) => sum + (Number(line.quantity) || 0), 0)}
              </Typography>
            </Box>
          </Box>

          <FieldArray
            name={
              isBatchManaged ? `docLine.${selectedLineIndex}.docLineBatchSn` : `docLine.${selectedLineIndex}.docLineBin`
            }
          >
            {({ remove, push }) => (
              <DatagridPremiumUi
                apiRef={apiRef}
                datagridIdentifier={isBatchManaged ? DataGridIdentifiers.BatchTable : DataGridIdentifiers.BinTable}
                height={480}
                columns={columns(setFieldValue, validateField, errors, remove)}
                rows={
                  isBatchManaged
                    ? values.docLine[selectedLineIndex]?.docLineBatchSn ?? []
                    : values.docLine[selectedLineIndex]?.docLineBin ?? []
                }
                editMode="row"
                slots={{
                  toolbar: isBatchManaged ? BatchToolbar : BinToolbar,
                }}
                slotProps={{
                  toolbar: isBatchManaged
                    ? {
                        disabled,
                        push,
                        defaultBatchLine,
                        apiRef,
                        selectedLineIndex,
                      }
                    : {
                        disabled,
                        push,
                      },
                }}
                onCellDoubleClick={(_params, event) => {
                  event.defaultMuiPrevented = true;
                }}
                headerFilters={false}
                getRowHeight={() => 'auto'}
                onCellClick={(params, event) => {
                  if (disabled) return;
                  const rowsInEditMode = Object.keys(apiRef.current.state.editRows);

                  for (const rowId of rowsInEditMode) {
                    if (params.row.id.toString() !== rowId) {
                      apiRef.current.stopRowEditMode({ id: rowId });
                    }
                  }

                  const rowMode = apiRef.current.getRowMode(params.row.id);
                  if (rowMode === GridRowModes.View) apiRef.current.startRowEditMode({ id: params.row.id });
                }}
              />
            )}
          </FieldArray>

          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center', gap: 2 }}>
            <ButtonUI text="Mentés" className="bg-success" onClick={handleSave} />
            <ButtonUI text="Mégse" color="blue-gray" variant="gradient" onClick={handleCancel} />
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default BatchIn;
